import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { Category, ProductImage, ProductAttributes } from '../../types';
import { adminAPI } from '../../services/api';
import { getImageUrl } from '../../utils/imageUrl';
import RichTextEditor from '../common/RichTextEditor';
import './AdminFormStyles.css';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<any>; // Returns created/updated product with ID
  product?: any | null;
  categories: Category[];
}

const ProductFormModal: React.FC<ProductFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  product,
  categories
}) => {
  const [formData, setFormData] = useState({
    name_bn: '',
    name_en: '',
    slug: '',
    category: '',
    summary_bn: '',
    summary_en: '',
    description_bn: '',
    description_en: '',
    price: '',
    discount_price: '',
    stock: '',
    image: null as File | null,
    is_active: true,
    is_featured: false,
    free_delivery: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  
  // Multi-image state
  const [existingImages, setExistingImages] = useState<ProductImage[]>([]);
  const [additionalImages, setAdditionalImages] = useState<File[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  
  // Attributes state
  const defaultAttributes: ProductAttributes = {
    colors:  { enabled: false, options: [] },
    sizes:   { enabled: false, options: [] },
    weights: { enabled: false, options: [] },
  };
  const [attributes, setAttributes] = useState<ProductAttributes>(defaultAttributes);
  const [attrInputs, setAttrInputs] = useState({ colors: '', sizes: '', weights: '' });

  // Add key for forcing RichTextEditor re-render
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (product) {
      console.log('Editing product:', product);
      console.log('Description BN length:', product.description_bn?.length || 0);
      console.log('Description EN length:', product.description_en?.length || 0);
      console.log('Product images:', product.images);
      console.log('Number of images:', product.images?.length || 0);
      
      const newFormData = {
        name_bn: product.name_bn || '',
        name_en: product.name_en || '',
        slug: product.slug || '',
        category: product.category?.id || product.category || '',
        summary_bn: product.summary_bn || '',
        summary_en: product.summary_en || '',
        description_bn: product.description_bn || '',
        description_en: product.description_en || '',
        price: product.price || '',
        discount_price: product.discount_price || '',
        stock: product.stock || '',
        image: null,
        is_active: product.is_active ?? true,
        is_featured: product.is_featured ?? false,
        free_delivery: product.free_delivery ?? false
      };
      
      console.log('Setting formData with descriptions:', {
        bn: newFormData.description_bn.substring(0, 100),
        en: newFormData.description_en.substring(0, 100)
      });
      
      setFormData(newFormData);
      // Load existing images
      const images = product.images || [];
      console.log('Setting existingImages:', images);
      setExistingImages(images);
      // Load existing attributes
      setAttributes({
        colors:  product.attributes?.colors  ?? { enabled: false, options: [] },
        sizes:   product.attributes?.sizes   ?? { enabled: false, options: [] },
        weights: product.attributes?.weights ?? { enabled: false, options: [] },
      });
    } else {
      setFormData({
        name_bn: '',
        name_en: '',
        slug: '',
        category: '',
        summary_bn: '',
        summary_en: '',
        description_bn: '',
        description_en: '',
        price: '',
        discount_price: '',
        stock: '',
        image: null,
        is_active: true,
        is_featured: false,
        free_delivery: false
      });
      setExistingImages([]);
      setAttributes({ colors: { enabled: false, options: [] }, sizes: { enabled: false, options: [] }, weights: { enabled: false, options: [] } });
    }
    setAdditionalImages([]);
    setAttrInputs({ colors: '', sizes: '', weights: '' });
    setErrors({});
    // Increment key synchronously to force RichTextEditor remount
    setEditorKey(prev => prev + 1);
  }, [product, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    if (type === 'file') {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        setFormData(prev => ({
          ...prev,
          image: files[0]
        }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const handleRichTextChange = (name: string, value: string) => {
    // Guard: don't let Quill's initialization onChange overwrite real content with empty
    const isQuillEmpty = (html: string) => {
      if (!html) return true;
      return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim() === '';
    };

    setFormData(prev => {
      const current = prev[name as keyof typeof prev];
      // If Quill fires onChange with empty content but formData already has real content, skip
      if (typeof current === 'string' && current.length > 15 && isQuillEmpty(value)) {
        return prev;
      }
      return { ...prev, [name]: value };
    });

    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  // Helper function to check if HTML content is empty
  const isHtmlEmpty = (html: string): boolean => {
    if (!html || html.trim() === '') return true;
    
    // Remove HTML tags and check if there's actual content
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text === '';
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setAdditionalImages(prev => [...prev, ...Array.from(files)]);
    }
  };

  const removeAdditionalImage = (index: number) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingImage = async (imageId: number) => {
    if (!product?.id) return;
    
    try {
      await adminAPI.deleteProductImage(product.id, imageId);
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Failed to delete image:', error);
    }
  };

  const generateSlug = () => {
    const slug = formData.name_en
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  // ── Attribute helpers ─────────────────────────────────────────────
  type AttrKey = 'colors' | 'sizes' | 'weights';

  const toggleAttr = (key: AttrKey) => {
    setAttributes(prev => ({
      ...prev,
      [key]: { ...prev[key]!, enabled: !prev[key]!.enabled },
    }));
  };

  const addAttrOption = (key: AttrKey) => {
    const val = attrInputs[key].trim();
    if (!val) return;
    setAttributes(prev => {
      const existing = prev[key]!.options;
      if (existing.includes(val)) return prev;
      return { ...prev, [key]: { ...prev[key]!, options: [...existing, val] } };
    });
    setAttrInputs(prev => ({ ...prev, [key]: '' }));
  };

  const removeAttrOption = (key: AttrKey, option: string) => {
    setAttributes(prev => ({
      ...prev,
      [key]: { ...prev[key]!, options: prev[key]!.options.filter(o => o !== option) },
    }));
  };

  const handleAttrKeyDown = (key: AttrKey, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); addAttrOption(key); }
  };
  // ─────────────────────────────────────────────────────────────────

  const validate = () => {
    const newErrors: any = {};

    if (!formData.name_bn.trim()) newErrors.name_bn = 'Bangla name is required';
    if (!formData.name_en.trim()) newErrors.name_en = 'English name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.category) newErrors.category = 'Category is required';
    
    // Validate rich text content
    if (isHtmlEmpty(formData.description_bn)) {
      newErrors.description_bn = 'Bangla description is required';
    }
    if (isHtmlEmpty(formData.description_en)) {
      newErrors.description_en = 'English description is required';
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) newErrors.price = 'Price must be greater than 0';
    if (formData.discount_price && parseFloat(formData.discount_price) >= parseFloat(formData.price)) {
      newErrors.discount_price = 'Discount price must be less than regular price';
    }
    if (!formData.stock || parseInt(formData.stock) < 0) newErrors.stock = 'Stock must be 0 or greater';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      const data = new FormData();
      data.append('name_bn', formData.name_bn);
      data.append('name_en', formData.name_en);
      data.append('slug', formData.slug);
      data.append('category', formData.category);
      
      // Clean up HTML content - if empty, send empty string
      const cleanSummaryBn = isHtmlEmpty(formData.summary_bn) ? '' : formData.summary_bn;
      const cleanSummaryEn = isHtmlEmpty(formData.summary_en) ? '' : formData.summary_en;
      const cleanDescriptionBn = isHtmlEmpty(formData.description_bn) ? '' : formData.description_bn;
      const cleanDescriptionEn = isHtmlEmpty(formData.description_en) ? '' : formData.description_en;
      
      data.append('summary_bn', cleanSummaryBn);
      data.append('summary_en', cleanSummaryEn);
      data.append('description_bn', cleanDescriptionBn);
      data.append('description_en', cleanDescriptionEn);
      data.append('price', formData.price);
      if (formData.discount_price) data.append('discount_price', formData.discount_price);
      data.append('stock', formData.stock);
      data.append('is_active', formData.is_active.toString());
      data.append('is_featured', formData.is_featured.toString());
      data.append('free_delivery', formData.free_delivery.toString());

      // Append attributes as JSON string (parsed by backend)
      data.append('attributes', JSON.stringify(attributes));

      if (formData.image instanceof File) {
        data.append('image', formData.image);
      }

      // onSave returns the created/updated product
      const savedProduct = await onSave(data);
      
      // Upload additional images if any
      // Use product.id for existing products, or savedProduct.id for newly created
      const productId = product?.id || savedProduct?.id;
      if (additionalImages.length > 0 && productId) {
        setImageUploading(true);
        try {
          await adminAPI.uploadProductImages(productId, additionalImages);
        } catch (imgError) {
          console.error('Failed to upload additional images:', imgError);
        }
        setImageUploading(false);
      }
      
      onClose();
    } catch (error: any) {
      console.error('Form submission error:', error);
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={product ? 'Edit Product' : 'Add Product'}
      size="large"
    >
      <form onSubmit={handleSubmit} className="admin-form-modal">
        <div className="admin-form-table">
          {/* Name Bangla */}
          <div className="admin-form-row">
            <div className="admin-form-label">Name (Bangla) *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="name_bn"
                value={formData.name_bn}
                onChange={handleChange}
                placeholder="পণ্যের নাম লিখুন"
                className={errors.name_bn ? 'has-error' : ''}
              />
              {errors.name_bn && <span className="error-text">{errors.name_bn}</span>}
            </div>
          </div>

          {/* Name English */}
          <div className="admin-form-row">
            <div className="admin-form-label">Name (English) *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="name_en"
                value={formData.name_en}
                onChange={handleChange}
                onBlur={generateSlug}
                placeholder="Enter product name"
                className={errors.name_en ? 'has-error' : ''}
              />
              {errors.name_en && <span className="error-text">{errors.name_en}</span>}
            </div>
          </div>

          {/* Slug */}
          <div className="admin-form-row">
            <div className="admin-form-label">Slug *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="product-url-slug"
                className={errors.slug ? 'has-error' : ''}
              />
              {errors.slug && <span className="error-text">{errors.slug}</span>}
              <span className="admin-form-hint">Auto-generated from English name</span>
            </div>
          </div>

          {/* Category */}
          <div className="admin-form-row">
            <div className="admin-form-label">Category *</div>
            <div className="admin-form-input">
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={errors.category ? 'has-error' : ''}
              >
                <option value="">Select Category</option>
                {Array.isArray(categories) && categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name_en}
                  </option>
                ))}
              </select>
              {errors.category && <span className="error-text">{errors.category}</span>}
            </div>
          </div>

          {/* Main Product Image */}
          <div className="admin-form-row">
            <div className="admin-form-label">Main Image</div>
            <div className="admin-form-input">
              <input
                type="file"
                name="image"
                onChange={handleChange}
                accept="image/*"
              />
              {formData.image && (
                <span className="admin-form-hint">Selected: {formData.image.name}</span>
              )}
              {product?.image && !formData.image && (
                <div style={{ marginTop: '8px' }}>
                  <img 
                    src={getImageUrl(product.image)} 
                    alt="Current" 
                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                  />
                  <span className="admin-form-hint" style={{ marginLeft: '8px' }}>Current main image</span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Images Section - Always show for gallery uploads */}
          <div className="admin-form-row">
            <div className="admin-form-label">Gallery Images</div>
            <div className="admin-form-input">
              {/* Existing Images Grid - Only when editing */}
              {product?.id && existingImages.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '10px', 
                  marginBottom: '12px',
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  {existingImages.map((img) => (
                    <div key={img.id} style={{ position: 'relative' }}>
                      <img 
                        src={getImageUrl(img.image)} 
                        alt="Product" 
                        style={{ 
                          width: '70px', 
                          height: '70px', 
                          objectFit: 'cover', 
                          borderRadius: '6px',
                          border: img.is_primary ? '2px solid #10b981' : '1px solid #e5e7eb'
                        }}
                      />
                      {img.is_primary && (
                        <span style={{
                          position: 'absolute',
                          top: '-6px',
                          left: '-6px',
                          background: '#10b981',
                          color: 'white',
                          fontSize: '10px',
                          padding: '2px 4px',
                          borderRadius: '4px'
                        }}>
                          ★
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingImage(img.id)}
                        style={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-6px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add More Images */}
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleAdditionalImagesChange}
                style={{ marginBottom: '8px' }}
              />
              <span className="admin-form-hint">Select multiple images to add to gallery</span>

              {/* Preview of new images to upload */}
              {additionalImages.length > 0 && (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px', 
                  marginTop: '12px',
                  padding: '12px',
                  background: '#fef3c7',
                  borderRadius: '8px',
                  border: '1px dashed #d97706'
                }}>
                  <span style={{ fontSize: '12px', color: '#92400e', width: '100%', marginBottom: '4px' }}>
                    New images to upload ({additionalImages.length}):
                  </span>
                  {additionalImages.map((file, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`New ${index + 1}`}
                        style={{ 
                          width: '60px', 
                          height: '60px', 
                          objectFit: 'cover', 
                          borderRadius: '6px',
                          border: '1px solid #d97706'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeAdditionalImage(index)}
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          fontSize: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Summary Bangla */}
          <div className="admin-form-row">
            <div className="admin-form-label">Summary (BN)</div>
            <div className="admin-form-input">
              <RichTextEditor
                key={`summary-bn-${product?.id || 'new'}-${editorKey}`}
                value={product?.summary_bn || ''}
                onChange={(value) => handleRichTextChange('summary_bn', value)}
                placeholder="সংক্ষিপ্ত বিবরণ..."
                minHeight="150px"
              />
              <span className="admin-form-hint">Product summary for quick overview</span>
            </div>
          </div>

          {/* Summary English */}
          <div className="admin-form-row">
            <div className="admin-form-label">Summary (EN)</div>
            <div className="admin-form-input">
              <RichTextEditor
                key={`summary-en-${product?.id || 'new'}-${editorKey}`}
                value={product?.summary_en || ''}
                onChange={(value) => handleRichTextChange('summary_en', value)}
                placeholder="Brief summary..."
                minHeight="150px"
              />
              <span className="admin-form-hint">Product summary for quick overview</span>
            </div>
          </div>

          {/* Description Bangla */}
          <div className="admin-form-row">
            <div className="admin-form-label">Description (BN) *</div>
            <div className="admin-form-input">
              <RichTextEditor
                key={`description-bn-${product?.id || 'new'}-${editorKey}`}
                value={product?.description_bn || ''}
                onChange={(value) => handleRichTextChange('description_bn', value)}
                placeholder="পণ্যের বিস্তারিত বর্ণনা..."
                className={errors.description_bn ? 'has-error' : ''}
                minHeight="250px"
              />
              {errors.description_bn && <span className="error-text">{errors.description_bn}</span>}
            </div>
          </div>

          {/* Description English */}
          <div className="admin-form-row">
            <div className="admin-form-label">Description (EN) *</div>
            <div className="admin-form-input">
              <RichTextEditor
                key={`description-en-${product?.id || 'new'}-${editorKey}`}
                value={product?.description_en || ''}
                onChange={(value) => handleRichTextChange('description_en', value)}
                placeholder="Enter product description..."
                className={errors.description_en ? 'has-error' : ''}
                minHeight="250px"
              />
              {errors.description_en && <span className="error-text">{errors.description_en}</span>}
            </div>
          </div>

          {/* Price */}
          <div className="admin-form-row">
            <div className="admin-form-label">Price (৳) *</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
                className={errors.price ? 'has-error' : ''}
              />
              {errors.price && <span className="error-text">{errors.price}</span>}
            </div>
          </div>

          {/* Discount Price */}
          <div className="admin-form-row">
            <div className="admin-form-label">Discount Price (৳)</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="discount_price"
                value={formData.discount_price}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
                className={errors.discount_price ? 'has-error' : ''}
              />
              {errors.discount_price && <span className="error-text">{errors.discount_price}</span>}
            </div>
          </div>

          {/* Stock */}
          <div className="admin-form-row">
            <div className="admin-form-label">Stock *</div>
            <div className="admin-form-input">
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                min="0"
                placeholder="0"
                className={errors.stock ? 'has-error' : ''}
              />
              {errors.stock && <span className="error-text">{errors.stock}</span>}
            </div>
          </div>

          {/* ── Product Attributes ───────────────────────────────────────── */}
          <div className="admin-form-row">
            <div className="admin-form-label" style={{ paddingTop: '8px' }}>Attributes</div>
            <div className="admin-form-input">
              <div className="attr-section">
                <p className="attr-section-hint">
                  Enable optional attributes (color, size, weight) for this product. Leave disabled if not needed.
                </p>

                {/* Color */}
                <div className="attr-group">
                  <label className="attr-toggle">
                    <input
                      type="checkbox"
                      checked={attributes.colors?.enabled ?? false}
                      onChange={() => toggleAttr('colors')}
                    />
                    <span className="attr-toggle-label">
                      <span className="attr-swatch" style={{ background: 'linear-gradient(135deg,#f87171,#60a5fa,#34d399)' }}></span>
                      Color
                    </span>
                  </label>
                  {attributes.colors?.enabled && (
                    <div className="attr-options">
                      <div className="attr-chips">
                        {(attributes.colors.options || []).map(opt => (
                          <span key={opt} className="attr-chip color-chip">
                            {opt}
                            <button type="button" onClick={() => removeAttrOption('colors', opt)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="attr-add-row">
                        <input
                          type="text"
                          value={attrInputs.colors}
                          onChange={e => setAttrInputs(prev => ({ ...prev, colors: e.target.value }))}
                          onKeyDown={e => handleAttrKeyDown('colors', e)}
                          placeholder="e.g. Red, Blue, Green..."
                        />
                        <button type="button" className="attr-add-btn" onClick={() => addAttrOption('colors')}>+ Add</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Size */}
                <div className="attr-group">
                  <label className="attr-toggle">
                    <input
                      type="checkbox"
                      checked={attributes.sizes?.enabled ?? false}
                      onChange={() => toggleAttr('sizes')}
                    />
                    <span className="attr-toggle-label">
                      <span className="attr-icon">📐</span>
                      Size
                    </span>
                  </label>
                  {attributes.sizes?.enabled && (
                    <div className="attr-options">
                      <div className="attr-chips">
                        {(attributes.sizes.options || []).map(opt => (
                          <span key={opt} className="attr-chip size-chip">
                            {opt}
                            <button type="button" onClick={() => removeAttrOption('sizes', opt)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="attr-add-row">
                        <input
                          type="text"
                          value={attrInputs.sizes}
                          onChange={e => setAttrInputs(prev => ({ ...prev, sizes: e.target.value }))}
                          onKeyDown={e => handleAttrKeyDown('sizes', e)}
                          placeholder="e.g. S, M, L, XL, XXL..."
                        />
                        <button type="button" className="attr-add-btn" onClick={() => addAttrOption('sizes')}>+ Add</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Weight */}
                <div className="attr-group">
                  <label className="attr-toggle">
                    <input
                      type="checkbox"
                      checked={attributes.weights?.enabled ?? false}
                      onChange={() => toggleAttr('weights')}
                    />
                    <span className="attr-toggle-label">
                      <span className="attr-icon">⚖️</span>
                      Weight
                    </span>
                  </label>
                  {attributes.weights?.enabled && (
                    <div className="attr-options">
                      <div className="attr-chips">
                        {(attributes.weights.options || []).map(opt => (
                          <span key={opt} className="attr-chip weight-chip">
                            {opt}
                            <button type="button" onClick={() => removeAttrOption('weights', opt)}>×</button>
                          </span>
                        ))}
                      </div>
                      <div className="attr-add-row">
                        <input
                          type="text"
                          value={attrInputs.weights}
                          onChange={e => setAttrInputs(prev => ({ ...prev, weights: e.target.value }))}
                          onKeyDown={e => handleAttrKeyDown('weights', e)}
                          placeholder="e.g. 250g, 500g, 1kg..."
                        />
                        <button type="button" className="attr-add-btn" onClick={() => addAttrOption('weights')}>+ Add</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* ─────────────────────────────────────────────────────────────── */}

          {/* Active Status */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Status</div>            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span>Active Product</span>
              </label>
            </div>
          </div>

          {/* Featured */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Featured</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleChange}
                />
                <span>Featured Product</span>
              </label>
            </div>
          </div>

          {/* Free Delivery */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Free Delivery</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="free_delivery"
                  checked={formData.free_delivery}
                  onChange={handleChange}
                />
                <span>Free Home Delivery</span>
              </label>
            </div>
          </div>
        </div>

        <div className="admin-form-actions">
          <button type="button" onClick={onClose} className="btn-cancel" disabled={loading || imageUploading}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading || imageUploading}>
            {loading ? 'Saving...' : imageUploading ? 'Uploading Images...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ProductFormModal;
